import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { FiCheck, FiFolder, FiFolderMinus, FiPlus, FiSearch } from "react-icons/fi";
import { UNFILED } from "../historyService";
import { useOutsidePress } from "../useHistory";

const MAX_FOLDER_LEN = 60;

// The folder button in a row's action cluster, and the menu it opens: a
// popover from sm up, a bottom sheet below. One field both filters the
// folders and names a new one.
export function MoveToFolderMenu({ conv, folders, language, open, busy, error, onToggle, onCancel, onSave }) {
  const zh = language === "zh";
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  useOutsidePress(rootRef, open, () => {
    if (!busy) onCancel();
  });

  return (
    <div ref={rootRef} className="relative flex-shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={busy && !open}
        title={zh ? "移至資料夾" : "Move to folder"}
        aria-label={zh ? "移至資料夾" : "Move to folder"}
        onClick={onToggle}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-all disabled:opacity-40 ${
          open
            ? "text-orange-600 bg-orange-50 dark:text-orange-300 dark:bg-orange-500/15"
            : "text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
        }`}
      >
        <FiFolder aria-hidden="true" className="w-4 h-4" />
      </button>
      {open && (
        <MovePanel
          conv={conv}
          folders={folders}
          language={language}
          busy={busy}
          error={error}
          anchorRef={rootRef}
          onSave={onSave}
          onCancel={onCancel}
          onEscape={() => {
            onCancel();
            triggerRef.current?.focus();
          }}
        />
      )}
    </div>
  );
}

// Mounted only while open, so each opening starts from an empty search and a
// failed move keeps what was typed.
function MovePanel({ conv, folders, language, busy, error, anchorRef, onSave, onCancel, onEscape }) {
  const zh = language === "zh";
  const panelRef = useRef(null);
  const listId = useId();
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const [validationError, setValidationError] = useState("");
  const [openUp, setOpenUp] = useState(false);

  // Opens upward when the row sits too close to the bottom of the window.
  useLayoutEffect(() => {
    const anchor = anchorRef.current?.getBoundingClientRect();
    const height = panelRef.current?.offsetHeight || 0;
    if (anchor) setOpenUp(anchor.bottom + height + 8 > window.innerHeight && anchor.top > height + 8);
  }, [anchorRef]);

  const fold = (text) => (text || "").toLocaleLowerCase();
  const choices = conv.folder && !folders.includes(conv.folder) ? [...folders, conv.folder] : folders;
  const typed = query.trim();
  const options = [
    ...choices
      .filter((folder) => fold(folder).includes(fold(typed)))
      .map((folder) => ({ kind: "folder", folder, current: fold(folder) === fold(conv.folder) })),
    ...(typed && !choices.some((folder) => fold(folder) === fold(typed)) ? [{ kind: "create", folder: typed }] : []),
    ...(conv.folder && !typed ? [{ kind: "remove", folder: "" }] : []),
  ];
  const active = Math.min(index, options.length - 1);

  const choose = (option) => {
    if (busy || !option) return;
    if (option.kind === "create" && option.folder === UNFILED) {
      setValidationError(zh ? "請輸入其他有效的資料夾名稱。" : "Enter a valid folder name.");
      return;
    }
    onSave(option.folder);
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      if (!busy) onEscape();
      return;
    }
    if (!options.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIndex((active + 1) % options.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setIndex((active - 1 + options.length) % options.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      choose(options[active]);
    }
  };

  const label = (option) => {
    if (option.kind === "create") return zh ? `建立「${option.folder}」` : `Create “${option.folder}”`;
    if (option.kind === "remove") return zh ? "移出資料夾" : "Remove from folder";
    return option.folder;
  };
  const Icon = { folder: FiFolder, create: FiPlus, remove: FiFolderMinus };

  return (
    <>
      <div aria-hidden="true" onClick={busy ? undefined : onCancel} className="fixed inset-0 z-40 bg-black/40 sm:hidden" />
      <div
        ref={panelRef}
        role="dialog"
        aria-label={zh ? "移至資料夾" : "Move to folder"}
        onKeyDown={onKeyDown}
        className={`fixed inset-x-0 bottom-0 z-50 p-3 pb-6 rounded-t-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl text-left sm:absolute sm:left-auto sm:right-0 sm:z-30 sm:w-72 sm:p-1.5 sm:rounded-xl ${
          openUp ? "sm:bottom-full sm:mb-1" : "sm:top-full sm:bottom-auto sm:mt-1"
        }`}
      >
        <div aria-hidden="true" className="sm:hidden mx-auto mb-3 h-1 w-9 rounded-full bg-gray-300 dark:bg-gray-600" />
        <p className="px-2 pt-1 pb-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
          {zh ? "移至資料夾" : "Move to folder"}
        </p>
        <div className="mx-0.5 mb-1.5 flex items-center gap-2 px-2.5 rounded-lg border border-gray-300 dark:border-gray-600 focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-transparent">
          <FiSearch aria-hidden="true" className="w-4 h-4 shrink-0 text-gray-400" />
          <input
            autoFocus
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={options.length ? `${listId}-${active}` : undefined}
            aria-label={zh ? "搜尋或建立資料夾" : "Find or create a folder"}
            placeholder={zh ? "搜尋或建立資料夾" : "Find or create a folder"}
            maxLength={MAX_FOLDER_LEN}
            readOnly={busy}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setIndex(0);
              setValidationError("");
            }}
            className="flex-1 min-w-0 py-2 bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 outline-none"
          />
        </div>
        {options.length > 0 ? (
          <ul id={listId} role="listbox" aria-label={zh ? "資料夾" : "Folders"} className="max-h-56 sm:max-h-52 overflow-y-auto flex flex-col gap-px">
            {options.map((option, i) => {
              const OptionIcon = Icon[option.kind];
              const selected = i === active;
              return (
                <React.Fragment key={`${option.kind}:${option.folder}`}>
                  {option.kind !== "folder" && i > 0 && (
                    <li role="presentation" className="my-1 mx-1 h-px bg-gray-100 dark:bg-gray-700" />
                  )}
                  <li
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={busy || undefined}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setIndex(i)}
                    onClick={() => choose(option)}
                    className={`flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm cursor-pointer ${
                      selected ? "bg-orange-50 text-orange-800 dark:bg-orange-500/15 dark:text-orange-200" : "text-gray-700 dark:text-gray-200"
                    } ${option.kind === "create" ? "font-medium" : ""}`}
                  >
                    <OptionIcon aria-hidden="true" className={`w-4 h-4 shrink-0 ${selected ? "text-orange-500" : "text-gray-400"}`} />
                    <span className="flex-1 min-w-0 break-words">{label(option)}</span>
                    {option.current && (
                      <span className="shrink-0 inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        <FiCheck aria-hidden="true" className="w-3.5 h-3.5 text-orange-500" />
                        {zh ? "目前" : "Current"}
                      </span>
                    )}
                  </li>
                </React.Fragment>
              );
            })}
          </ul>
        ) : (
          <p className="px-2 py-2 text-sm text-gray-500 dark:text-gray-400">
            {zh ? "輸入名稱以建立第一個資料夾。" : "Type a name to create the first folder."}
          </p>
        )}
        {(validationError || error) && (
          <p role="alert" className="px-2 pt-2 text-sm text-red-600 dark:text-red-400">
            {validationError || error}
          </p>
        )}
        {busy ? (
          <p role="status" className="px-2 pt-2 pb-1 text-xs text-gray-500 dark:text-gray-400">
            {zh ? "移動中…" : "Moving…"}
          </p>
        ) : (
          <p className="hidden sm:flex gap-3 mt-1.5 px-2 pt-1.5 pb-0.5 border-t border-gray-100 dark:border-gray-700 text-[11px] text-gray-400 dark:text-gray-500">
            <span>↑↓ {zh ? "選擇" : "choose"}</span>
            <span>Enter {zh ? "移動" : "move"}</span>
            <span>Esc {zh ? "關閉" : "close"}</span>
          </p>
        )}
      </div>
    </>
  );
}
